import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import ATSCalendarModal from "@/components/ats/ATSCalendarModal";
import { DEMO_CALENDAR_URL, APP_NAME, APP_URL, SOCIAL_LINKS } from "@/constants";
import { trackDemoRequest } from "@/utils/analytics";

const pageJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${APP_URL}/#organization`,
      "name": APP_NAME,
      "alternateName": "Reclutamiento Digital S.A.",
      "url": APP_URL,
      "logo": `${APP_URL}/lovable-uploads/a2e4d3de-cc00-4222-a473-67c9a76aed45.png`,
      "description": "Ecosistema de reclutamiento que une IA, psicometría y hunting para reducir 50% el tiempo de contratación en Costa Rica y Centroamérica.",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+506-8886-2011",
        "contactType": "customer service",
        "availableLanguage": "Spanish",
        "areaServed": ["CR", "GT", "PA"]
      },
      "sameAs": [
        SOCIAL_LINKS.FACEBOOK,
        SOCIAL_LINKS.INSTAGRAM,
        SOCIAL_LINKS.LINKEDIN
      ],
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "CR",
        "addressRegion": "San José",
        "addressLocality": "Escazú"
      }
    },
    {
      "@type": "WebPage",
      "@id": `${APP_URL}/#agendar-demo`,
      "url": `${APP_URL}/#agendar-demo`,
      "name": "Agende una demo de Nexus ATS | Encuentre al candidato ideal — Nexus HR",
      "description": "Reserve una demo personalizada de Nexus ATS. Descubra cómo nuestra IA, psicometría y hunting reducen 50% el tiempo de contratación en Costa Rica.",
      "inLanguage": "es-CR",
      "isPartOf": {
        "@type": "WebSite",
        "url": APP_URL
      },
      "publisher": {
        "@id": `${APP_URL}/#organization`
      },
      "about": {
        "@type": "Service",
        "name": "Demo de Nexus ATS",
        "description": "Demostración personalizada del sistema ATS de Nexus HR con IA, psicometría y hunting de talento.",
        "provider": {
          "@id": `${APP_URL}/#organization`
        }
      },
      "primaryImageOfPage": {
        "@type": "ImageObject",
        "url": `${APP_URL}/og-image.jpg`,
        "width": 1200,
        "height": 630
      }
    }
  ]
};

const FinalCTA: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Helmet>
        {/* Título, descripción y etiquetas sociales los define SEOMetaTags por ruta.
            Aquí solo se aporta el JSON-LD de la sección de demo. */}
        <script type="application/ld+json">
          {JSON.stringify(pageJsonLd)}
        </script>
      </Helmet>
      <section
        id="agendar-demo"
        aria-labelledby="agendar-demo-heading"
        className="relative overflow-hidden"
        style={{ background: "#0F1035", paddingTop: "64px", paddingBottom: "64px" }}
      >
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(98, 10, 226, 0.4) 0%, transparent 70%)",
        }}
      />
      <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          <h2
            id="agendar-demo-heading"
            className="font-exo font-bold text-white mb-4"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            Encuentre al candidato ideal para su empresa
          </h2>
          <p className="text-lg mb-8" style={{ color: "rgba(255,255,255,0.8)" }}>
            Reserve un espacio en nuestro calendario y un especialista le mostrará Nexus ATS
            con las vacantes reales de su empresa.
          </p>

          {/* Embedded calendar */}
          <div
            className="bg-white rounded-2xl shadow-2xl overflow-hidden mb-8"
            role="region"
            aria-label="Calendario de disponibilidad para agendar una demo de Nexus ATS"
          >
            <iframe
              src={DEMO_CALENDAR_URL}
              className="w-full border-0"
              style={{ height: "720px" }}
              title="Calendario para agendar demo de Nexus ATS con disponibilidad real"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>


          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={() => {
                trackDemoRequest("Homepage Final CTA");
                setOpen(true);
              }}
              aria-label="Abrir calendario para agendar una demo de Nexus ATS"
              className="rounded-md font-semibold h-12 px-6 bg-[#620AE2] text-white border-[1.5px] border-[#620AE2] hover:bg-white hover:text-[#620AE2] hover:border-[#620AE2] transition-all duration-200"
            >
              <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              Agendar demo
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </Button>
          </div>

        </div>
      </div>

      <ATSCalendarModal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Agendar demo de Nexus ATS"
        subtitle="Elija el horario que mejor le convenga"
      />
    </section>
    </>
  );
};

export default FinalCTA;
